<template>


      <v-expansion-panel class="upload-container">
        <v-expansion-panel-title >
          <div class="upload-header">
            <div class="upload-title-wrapper">
              <h2 class="upload-title">Bestanden uploaden of velden invullen</h2>
            </div>
            <p class="upload-description">
              Upload bestanden toe of vul de velden in
            </p>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <form
              @submit.prevent="handleSubmit"
              @dragenter.prevent="handleDragEnter"
              @dragleave.prevent="handleDragLeave"
              @dragover.prevent
              @drop.prevent="handleDrop"
          >
          <v-row>

            <div
                class="upload-dropzone"
                :class="{ 'upload-dropzone--active': state.dragActive }"
                tabindex="0"
                role="button"
                @keydown.enter="triggerFileInput"
                @keydown.space="triggerFileInput"
                @click="triggerFileInput"
            >
              <div class="dropzone-content">
                <img
                    loading="lazy"
                    src="/assets/upload.svg"
                    class="upload-icon"
                    alt="Upload icon"
                />
                <div class="dropzone-text">
                  <div class="dropzone-actions">
                    <button type="button" class="upload-button">Klik om te uploaden</button>
                    <span class="drag-text">of sleep hierheen</span>
                  </div>
                  <p class="file-types">.xltx .xlsx .xlsm .xlsb</p>
                </div>
              </div>
            </div>
          </v-row>
          <v-row>
            <div class="upload-queue" v-if="state.uploads.length">
              <UploadItem
                  :user-id="userId"
                  v-for="upload in state.uploads"
                  :key="upload.id"
                  :upload="upload"
                  @remove="removeFile(upload.id)"
              />
            </div>
          </v-row>

          <v-row>
            <input
                type="file"
                ref="fileInput"
                class="visually-hidden"
                accept=".xltx,.xlsx,.xlsm,.xlsb"
                @change="handleFileChange"
                multiple
            />
          </v-row>
          </form>
        </v-expansion-panel-text>
      </v-expansion-panel>
</template>

<script lang="ts" setup>
import {defineComponent, ref} from 'vue';
import {useFileUpload} from '../../composables/useFileUpload';
import UploadItem from '../../components/Modals/UploadItem.vue';
import {useAuth} from "../../composables/useAuth";
import router from '../../router';
import {API_BASE_URL} from "../../config";

const props = defineProps({
  expandPanel: Boolean,
  userId: {
    type: Number,
    required: false
  },
})
const activePanels = ref([0]); // Expand the first panel by default

const isExpanded = ref(props.expandPanel);
console.log(isExpanded.value)
const emit=defineEmits(['expand'])
const emitExpand = () => {
  // Emit the event to the parent
  emit('expand', isExpanded.value);
};
const fileInput = ref<HTMLInputElement | null>(null);
const {
  state,
  addFiles,
  removeFile,
  updateProgress,
  setDragActive
} = useFileUpload();

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files?.length) {
    addFiles(target.files);
    target.value = '';
  }
};

const handleDragEnter = () => {
  setDragActive(true);
};

const handleDragLeave = (event: DragEvent) => {
  if (event.target === event.currentTarget) {
    setDragActive(false);
  }
};

const handleDrop = (event: DragEvent) => {
  setDragActive(false);
  if (event.dataTransfer?.files.length) {
    addFiles(event.dataTransfer.files);

  }
};

const handleSubmit = async () => {
  if (!state.value.uploads.length) {
    console.log('No files to upload');
    return;
  }

  state.value.isUploading = true;

  for (const upload of state.value.uploads) {
    if (upload.status === 'pending') {
      try {
        console.log('Uploading file:', upload.file.name);

        const formData = new FormData();
        formData.append('file', upload.file);
        formData.append('fileType', 'financial'); // Add fileType field
        if (props.userId != null) {
          formData.append('userId', props.userId.toString());
        }

        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            console.log(`Progress for ${upload.file.name}: ${progress}%`);
            updateProgress(upload.id, progress);
          }
        };

        await new Promise(async (resolve, reject) => {

          let {getToken}=useAuth();
          // Ensure access token is available
          let accessToken = await getToken().catch((error) => {
            router.push({ name: 'login' });
          });
          xhr.onload = () => {
            if (xhr.status === 200) {
              console.log(`File ${upload.file.name} uploaded successfully`);
              resolve(xhr.response);
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          };
          xhr.onerror = () => {
            reject(new Error('Network error during file upload'));
          };
          xhr.open('POST', `${API_BASE_URL}/api/excel/upload`);
          xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
          xhr.send(formData);
        });
      } catch (error) {
        console.error('Upload failed:', error);
        upload.status = 'error';
      }
    }
  }

  state.value.isUploading = false;
};
</script>

<style scoped>
.upload-container {
  border-radius: 16px;
  border: 1px solid var(--Neutral-6, #c7c8cc);
  display: flex;
  margin-top: 24px;
  width: 100%;
  flex-direction: column;
}

.upload-header {
  display: flex;
  width: 100%;
  flex-direction: column;
  font-family: Montserrat, sans-serif;
}

.upload-title-wrapper {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}

.upload-title {
  font-size: 16px;
  color: var(--Neutral-5, #191a1f);
  font-weight: 700;
  line-height: 2;
  flex: 1;
  margin: 0;
}

.info-icon {
  width: 20px;
  height: 20px;
}

.upload-description {
  color: var(--Neutral-7, #787a87);
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  margin: 8px 0 0;
}

.upload-dropzone {
  border-radius: 8px;
  border: 2px dashed var(--Neutral-6, #c7c8cc);
  background-color: #fff;
  margin-top: 20px;
  width: 100%;
  padding: 16px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-dropzone--active {
  border-color: var(--Primary-Primary, #080aed);
  background-color: rgba(8, 10, 237, 0.05);
}

.upload-dropzone:focus {
  outline: none;
  border-color: var(--Primary-Primary, #080aed);
  box-shadow: 0 0 0 2px rgba(8, 10, 237, 0.2);
}

.dropzone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon {
  width: 40px;
  height: 40px;
}

.dropzone-text {
  display: flex;
  margin-top: 12px;
  width: 100%;
  flex-direction: column;
  align-items: center;
}

.dropzone-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.upload-button {
  color: var(--Primary-Primary, #080aed);
  font-weight: 500;
  line-height: 1;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
}

.drag-text {
  color: var(--Neutral-7, #787a87);
  font-weight: 400;
}

.file-types {
  color: var(--Neutral-7, #787a87);
  text-align: center;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  margin: 4px 0 0;
}

.upload-queue {
  margin-top: 20px;
  width: 100%;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 991px) {
  .upload-container,
  .upload-header,
  .upload-title-wrapper,
  .upload-title,
  .upload-description,
  .upload-dropzone,
  .dropzone-content,
  .dropzone-text,
  .file-types,
  .upload-queue {
    max-width: 100%;
  }

  .upload-dropzone {
    padding: 16px 20px;
  }
}
</style>